<template>
  <div class="activity-stage">
    <section v-if="currentRound">
      <div class="whiteboard">
        <img :src="doctorImg" />
        <svg id="whiteboardSvg" width="80%" height="90%" v-html="whiteboardSvg"></svg>
        <svg id="doctorBubbleSvg" height="100px" width="200px" v-html="doctorBubbleSvg"></svg>
        <span
          v-if="bubbleText"
          id="bubbleText"
          :class="{
            shortText: bubbleText.length < 15
          }"
          v-text="bubbleText"
        ></span>
        <div id="sentence">
          <span v-if="!correctStep" v-text="currentRound.incorrectSentence"></span>
          <blinking-cursor-text
            v-else
            v-for="(step, index) in currentRound.steps"
            :key="`step${index}`"
            :text="step.answer"
            :text-step="index"
          ></blinking-cursor-text>
        </div>
      </div>
      <div class="choose">
        <span id="instructions">Choose the correct sentence. Then type the sentence.</span>
        <div
          v-for="(step, index) in currentRound.selectList"
          :key="`step${index}`"
          :class="['select', `s-${index}`]"
        >
          <checkbox-sentence
            :is-checked="step.isSelected"
            :has-error="!step.isCorrectAnswer"
            @click.native="selectAnswer(step, index)"
          ></checkbox-sentence>
          <div class="step" v-text="step.answer"></div>
        </div>
      </div>
    </section>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import ActivityManager from 'mixins/ActivityManager';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import CheckboxSentence from 'components/inputs/CheckboxSentence.vue';

import doctorImg from 'assets/images/activities/sentence-doctor/sentence-doctor-doc.png';
import whiteboardSvg from 'assets/images/activities/sentence-doctor/sentence-doctor-whiteboard.svg';
import doctorBubbleSvg from 'assets/images/activities/sentence-doctor/sentence-doctor-bubble.svg';

export default {
  mixins: [ActivityManager, AssetLoader, ErrorLogger, TypingActivityHelper],

  components: {
    BlinkingCursorText,
    CheckboxSentence
  },

  data() {
    return {
      activityName: 'Sentence Doctor',
      doctorImg,
      whiteboardSvg,
      doctorBubbleSvg,
      correctStep: undefined,
      answerIndex: undefined,
      bubbleText: undefined
    };
  },

  watch: {
    correctStep() {
      if (this.answerIndex !== undefined) {
        this.enableKeyboard();
        this.showKeyboard();

        for (let index = 0; index < this.currentRound.selectList.length; index += 1) {
          if (index !== this.answerIndex)
            TweenMax.staggerTo(`.s-${index}`, 0.2, { autoAlpha: 0, display: 'none' }, 0.2);
        }

        TweenMax.fromTo(
          '#sentence',
          1,
          { autoAlpha: 0 },
          {
            css: { autoAlpha: 1 },
            delay: 0.25
          }
        );
      }
    }
  },

  methods: {
    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: '',
          voice: `${this.voiceInstructionsPrefix}/sentencedoctor-${this.getRandomVoice()}-1.mp3`
        }
      };
    },

    async loadActivity() {
      const roundSetup = this.getActivityState();

      this.createRounds(roundSetup);
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setRound();
      this.errorLoggerStart();
    },

    setRound() {
      this.disableKeyboard();
      this.hideKeyboard();
      this.setupNewRound();
      this.$nextTick(() => {
        this.bubbleText = this.currentRound.startTextBubble;
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
        TweenMax.staggerFromTo('.select', 1.5, { autoAlpha: 0 }, { autoAlpha: 1 }, 0.2);
      });
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.bubbleText = this.currentRound.endTextBubble;
        this.setCurrentStep();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const t = new TimelineLite();

      t.delay(0.8).call(() => {
        if (this.isLastRound) {
          TweenMax.delayedCall(0.8, this.completeActivity);
        } else {
          t.delay(1)
            .to('.activity-stage', 0.8, { opacity: 0 })
            .call(this.resetSteps)
            .call(this.setRound);
        }
      });
    },

    selectAnswer(answer, i) {
      const step = this.currentRound.selectList[i];
      step.isSelected = true;

      if (answer.isCorrectAnswer) {
        this.answerIndex = i;
        this.correctStep = step;
        return this.sndBell();
      }
      this.currentRound.selectList.forEach(el => {
        if (el.answer !== step.answer) {
          // eslint-disable-next-line
          el.isSelected = false;
        }
      });
      return this.sndType();
    },

    resetSteps() {
      this.currentRound.selectList[this.answerIndex].isSelected = false;
      this.correctStep = undefined;
      this.answerIndex = undefined;
    }
  }
};
</script>

<style lang="scss" scoped>
section {
  font-family: hwtfutura;
  .whiteboard {
    img {
      position: absolute;
      z-index: 1;
      top: 6%;
      left: 1.5%;
      height: 400px;
    }
    #doctorBubbleSvg {
      position: relative;
      z-index: 0;
      margin-left: 3.5%;
    }
    #whiteboardSvg {
      position: absolute;
      z-index: 0;
      top: -33%;
      left: 10%;
    }
    #bubbleText {
      display: block;
      position: absolute;
      margin: -9% 8%;
      width: 122px;
      font-size: 0.85em;
      text-align: left;
    }
    .shortText {
      padding: 1% 2.5%;
    }
    #sentence {
      position: absolute;
      font-size: 2em;
      top: 5%;
      left: 23.5%;
      width: 55%;
      p,
      span {
        display: block;
        width: 100%;
        padding: 0 0.5%;
      }
    }
  }
  .choose {
    display: block;
    width: 70%;
    margin: 12% 25% auto;
    #instructions {
      font-family: hwtfutura-semibold;
      margin: 5% 0;
    }
    .select {
      margin: 2.5% 0;
      .step {
        display: inline-block;
        vertical-align: top;
        height: 35px;
        line-height: 35px;
      }
    }
  }
}
</style>
